import type React from 'react';

/**
 * GearArt — a matched SET of 7 premium stylized line-art broadcast-gear
 * illustrations for the LA Mirage Live landing "kit behind your broadcast"
 * showcase.
 *
 * Design language (spec A6 — keep the whole set consistent):
 *  - viewBox "0 0 120 120", art centred at ~86px optical size.
 *  - STROKE-ONLY line-art in champagne gold. Every visible stroke references
 *    `url(#gearGold)` — the shared gradient defined ONCE in <GearDefs/>, which
 *    the parent (GearShowcase) renders a single time. Sibling SVGs reference it.
 *  - stroke-width: 2 for primary silhouette, 1.25 for detail.
 *  - stroke-linejoin / stroke-linecap: round throughout.
 *  - The ONLY fills allowed: tiny glass lens/screen panes
 *    (fill = color-mix(in srgb, var(--lw-accent) 12%, transparent)) and
 *    1–2 small solid-gold record/sensor "glint" dots (r≈1.5) — the set's
 *    signature flourish.
 *
 * All gold comes from `var(--lw-accent)` via the gradient or the glass mix —
 * no hardcoded gold hex anywhere in this file.
 */

/** The tiny frosted glass fill used for lenses / screens. */
const GLASS = 'color-mix(in srgb, var(--lw-accent) 12%, transparent)';

/**
 * <defs> for the whole set. Rendered ONCE by the parent so a single
 * `#gearGold` gradient is shared by every sibling SVG on the page.
 * 135° champagne sweep: var(--lw-accent) → a lighter gold (--lw-accent-2).
 */
export function GearDefs(): React.ReactElement {
  return (
    <svg
      width={0}
      height={0}
      aria-hidden="true"
      focusable="false"
      style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
    >
      <defs>
        {/* 135° gold sweep: x1,y1 top-left → x2,y2 bottom-right. */}
        <linearGradient id="gearGold" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--lw-accent, #C9A962)" />
          <stop offset="55%" stopColor="var(--lw-accent-2, #E8C25A)" />
          <stop offset="100%" stopColor="var(--lw-accent-2, #F4D06F)" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/**
 * Shared attributes for every gear <svg> so the family reads consistently.
 * Strokes default to the gold gradient; individual detail paths dial down
 * stroke-width to 1.25.
 */
const svgBase = {
  viewBox: '0 0 120 120',
  role: 'img' as const,
  width: '100%',
  height: '100%',
  fill: 'none',
  stroke: 'url(#gearGold)',
  strokeWidth: 2,
  strokeLinejoin: 'round' as const,
  strokeLinecap: 'round' as const,
};

/* ------------------------------------------------------------------------ */
/* 1. YoloBox Ultra — live 4K switcher                                       */
/*    Wide landscape slate; large inset multiview screen split 4-up          */
/*    (2 vertical + 1 horizontal thin lines); left-edge stack of 4 port      */
/*    notches; bottom-right REC dot; one top-left glint.                     */
/* ------------------------------------------------------------------------ */
export function GearYoloboxArt(): React.ReactElement {
  return (
    <svg {...svgBase} aria-label="YoloBox Ultra live switcher">
      {/* body slate ~w96 x h60, centred */}
      <rect x="12" y="30" width="96" height="60" rx="7" />
      {/* inset multiview screen */}
      <rect x="30" y="40" width="72" height="40" rx="3" fill={GLASS} strokeWidth={1.25} />
      {/* 4-up multiview grid: 1 horizontal + 1 vertical divider */}
      <line x1="66" y1="40" x2="66" y2="80" strokeWidth={1.25} />
      <line x1="30" y1="60" x2="102" y2="60" strokeWidth={1.25} />
      {/* left-edge stack of 4 HDMI port notches */}
      <line x1="16" y1="44" x2="24" y2="44" strokeWidth={1.25} />
      <line x1="16" y1="52" x2="24" y2="52" strokeWidth={1.25} />
      <line x1="16" y1="60" x2="24" y2="60" strokeWidth={1.25} />
      <line x1="16" y1="68" x2="24" y2="68" strokeWidth={1.25} />
      {/* bottom-right REC dot (signature filled glint) */}
      <circle cx="94" cy="86" r="1.5" fill="url(#gearGold)" stroke="none" />
      {/* top-left status glint */}
      <circle cx="20" cy="36" r="1.5" fill="url(#gearGold)" stroke="none" />
    </svg>
  );
}

/* ------------------------------------------------------------------------ */
/* 2. Sony ZV-E10 — flat-top vlog mirrorless                                 */
/*    Boxy rangefinder body, FLAT TOP (no viewfinder hump); big front-left   */
/*    lens (2 concentric + glass); top 3-capsule mic in a bracket; thin      */
/*    flip-screen hint to the side; record dot top-right.                    */
/* ------------------------------------------------------------------------ */
export function GearZvE10Art(): React.ReactElement {
  return (
    <svg {...svgBase} aria-label="Sony ZV-E10 mirrorless camera">
      {/* body ~w62 x h44, flat top */}
      <rect x="26" y="44" width="62" height="44" rx="5" />
      {/* front-left lens: 2 concentric rings + glass */}
      <circle cx="48" cy="66" r="15" />
      <circle cx="48" cy="66" r="9" strokeWidth={1.25} />
      <circle cx="48" cy="66" r="4" fill={GLASS} strokeWidth={1.25} />
      {/* top 3-capsule mic in a bracket, sitting on the flat top */}
      <rect x="60" y="30" width="22" height="14" rx="3" strokeWidth={1.25} />
      <line x1="66" y1="33" x2="66" y2="41" strokeWidth={1.25} />
      <line x1="71" y1="33" x2="71" y2="41" strokeWidth={1.25} />
      <line x1="76" y1="33" x2="76" y2="41" strokeWidth={1.25} />
      {/* thin flip-out screen hint on the right side */}
      <rect x="88" y="52" width="8" height="26" rx="2" strokeWidth={1.25} />
      {/* record dot top-right of body */}
      <circle cx="80" cy="52" r="1.5" fill="url(#gearGold)" stroke="none" />
    </svg>
  );
}

/* ------------------------------------------------------------------------ */
/* 3. PTZ Camera — robotic pan-tilt-zoom                                      */
/*    Dome/head on a base plinth (low trapezoid); boxy pan-tilt head in a    */
/*    U-yoke; large offset lens (glass); two arc marks for rotation; IR      */
/*    glint.                                                                  */
/* ------------------------------------------------------------------------ */
export function GearPtzCamArt(): React.ReactElement {
  return (
    <svg {...svgBase} aria-label="Robotic PTZ camera">
      {/* base plinth: low trapezoid */}
      <path d="M40 96 L44 84 L76 84 L80 96 Z" />
      {/* neck riser from base to yoke */}
      <line x1="60" y1="84" x2="60" y2="78" />
      {/* U-yoke arms cradling the head */}
      <path d="M40 78 L40 56" strokeWidth={1.25} />
      <path d="M80 78 L80 56" strokeWidth={1.25} />
      <path d="M40 78 L80 78" strokeWidth={1.25} />
      {/* boxy pan-tilt head with rounded dome front */}
      <path d="M42 58 Q42 34 60 34 Q78 34 78 58 L78 62 L42 62 Z" />
      {/* large offset lens (toward left) + glass */}
      <circle cx="54" cy="50" r="11" strokeWidth={1.25} />
      <circle cx="54" cy="50" r="5" fill={GLASS} strokeWidth={1.25} />
      {/* two rotation arc marks flanking the head */}
      <path d="M30 50 A16 16 0 0 1 34 40" strokeWidth={1.25} />
      <path d="M90 50 A16 16 0 0 0 86 40" strokeWidth={1.25} />
      {/* IR sensor glint top-right of head */}
      <circle cx="72" cy="44" r="1.5" fill="url(#gearGold)" stroke="none" />
    </svg>
  );
}

/* ------------------------------------------------------------------------ */
/* 4. Lumix G7 — DSLR-style body WITH pentaprism hump                        */
/*    SLR silhouette with pentaprism hump top-centre (the key vs ZV-E10);    */
/*    deeper right grip bulge; big centre lens (2 rings + glass); mode dial  */
/*    (ticked circle top-right); hot-shoe bracket atop the hump.             */
/* ------------------------------------------------------------------------ */
export function GearLumixG7Art(): React.ReactElement {
  return (
    <svg {...svgBase} aria-label="Panasonic Lumix G7 camera">
      {/* body with pentaprism hump top-centre + deeper right grip bulge */}
      <path
        d="M28 48
           L50 48
           L54 34 L70 34 L74 48
           L92 48
           Q98 48 98 56
           L98 82
           Q98 90 90 90
           L34 90
           Q28 90 28 82
           Z"
      />
      {/* hot-shoe bracket atop the pentaprism hump */}
      <rect x="57" y="28" width="10" height="6" rx="1.5" strokeWidth={1.25} />
      {/* centre lens: 2 rings + glass */}
      <circle cx="58" cy="66" r="16" />
      <circle cx="58" cy="66" r="10" strokeWidth={1.25} />
      <circle cx="58" cy="66" r="4.5" fill={GLASS} strokeWidth={1.25} />
      {/* mode dial: ticked circle top-right */}
      <circle cx="86" cy="56" r="5" strokeWidth={1.25} />
      <line x1="86" y1="52" x2="86" y2="54" strokeWidth={1.25} />
      <line x1="86" y1="58" x2="86" y2="60" strokeWidth={1.25} />
      <line x1="82" y1="56" x2="84" y2="56" strokeWidth={1.25} />
      <line x1="88" y1="56" x2="90" y2="56" strokeWidth={1.25} />
      {/* record/AF glint on the grip */}
      <circle cx="88" cy="74" r="1.5" fill="url(#gearGold)" stroke="none" />
    </svg>
  );
}

/* ------------------------------------------------------------------------ */
/* 5. PTZ Controller — live-operation console                                */
/*    Shallow sloped-desk trapezoid panel; dominant joystick (vertical stalk */
/*    topped by a ball) centre-right; small LCD top-left (glass); 2x3 button */
/*    dots bottom-left; a rotary knob (ticked circle).                       */
/* ------------------------------------------------------------------------ */
export function GearPtzCtrlArt(): React.ReactElement {
  return (
    <svg {...svgBase} aria-label="PTZ camera controller console">
      {/* sloped-desk console: shallow trapezoid with a raised back edge */}
      <path d="M22 82 L30 46 L90 46 L98 82 Z" />
      {/* front lip */}
      <path d="M22 82 L22 88 L98 88 L98 82" strokeWidth={1.25} />
      {/* small LCD top-left (glass) */}
      <rect x="33" y="52" width="20" height="12" rx="2" fill={GLASS} strokeWidth={1.25} />
      {/* dominant joystick: base + vertical stalk + ball, centre-right */}
      <ellipse cx="74" cy="70" rx="9" ry="4" strokeWidth={1.25} />
      <line x1="74" y1="68" x2="74" y2="54" />
      <circle cx="74" cy="50" r="6" />
      {/* 2x3 button dots bottom-left */}
      <circle cx="36" cy="72" r="2" strokeWidth={1.25} />
      <circle cx="44" cy="72" r="2" strokeWidth={1.25} />
      <circle cx="52" cy="72" r="2" strokeWidth={1.25} />
      <circle cx="36" cy="79" r="2" strokeWidth={1.25} />
      <circle cx="44" cy="79" r="2" strokeWidth={1.25} />
      <circle cx="52" cy="79" r="2" strokeWidth={1.25} />
      {/* rotary knob (ticked circle) bottom-right */}
      <circle cx="90" cy="72" r="5" strokeWidth={1.25} />
      <line x1="90" y1="68" x2="90" y2="70" strokeWidth={1.25} />
      {/* status glint by the LCD */}
      <circle cx="58" cy="54" r="1.5" fill="url(#gearGold)" stroke="none" />
    </svg>
  );
}

/* ------------------------------------------------------------------------ */
/* 6. DJI Pocket 4 — pocket gimbal                                            */
/*    Slim vertical "pen" stick (~w22 x h74); TOP gimbal cradle = a tiny     */
/*    U-bracket holding a rounded camera head/lens offset in the yoke; a     */
/*    small 2" screen line on the body; a joystick nub; one lens glint.      */
/*    Kept delicate & small.                                                  */
/* ------------------------------------------------------------------------ */
export function GearPocket4Art(): React.ReactElement {
  return (
    <svg {...svgBase} aria-label="DJI Pocket 4 gimbal camera">
      {/* slim handle stick, centred (~w22 x h50 lower portion) */}
      <rect x="49" y="42" width="22" height="52" rx="6" />
      {/* small 2" screen line on the handle (glass) */}
      <rect x="53" y="50" width="14" height="18" rx="2" fill={GLASS} strokeWidth={1.25} />
      {/* joystick nub below the screen */}
      <circle cx="60" cy="80" r="3" strokeWidth={1.25} />
      {/* top gimbal cradle: tiny U-bracket */}
      <path d="M48 42 L48 30 Q48 24 54 24" strokeWidth={1.25} />
      <path d="M72 42 L72 30 Q72 24 66 24" strokeWidth={1.25} />
      {/* rounded camera head offset in the yoke */}
      <rect x="52" y="18" width="16" height="14" rx="4" strokeWidth={1.25} />
      {/* lens on the head + glass */}
      <circle cx="60" cy="25" r="4" fill={GLASS} strokeWidth={1.25} />
      {/* lens glint */}
      <circle cx="62" cy="23" r="1.5" fill="url(#gearGold)" stroke="none" />
    </svg>
  );
}

/* ------------------------------------------------------------------------ */
/* 7. Cinema / And more — box camera + "and more" motif                      */
/*    Boxier cinema/box camera body; large matte-box lens (big circle +      */
/*    squared hood rect); thin top handle arch; a "+" / film-reel motif for  */
/*    "and more."                                                            */
/* ------------------------------------------------------------------------ */
export function GearCineMoreArt(): React.ReactElement {
  return (
    <svg {...svgBase} aria-label="Cinema camera and more equipment">
      {/* boxy cinema body */}
      <rect x="30" y="46" width="46" height="42" rx="4" />
      {/* thin top handle arch */}
      <path d="M40 46 L40 38 Q40 34 44 34 L62 34 Q66 34 66 38 L66 46" strokeWidth={1.25} />
      {/* squared matte-box hood projecting forward from the lens */}
      <path d="M76 54 L96 50 L96 84 L76 80 Z" strokeWidth={1.25} />
      {/* large matte-box lens: ring + glass */}
      <circle cx="70" cy="67" r="14" />
      <circle cx="70" cy="67" r="7" fill={GLASS} strokeWidth={1.25} />
      {/* "and more" — a small film-reel motif on the body */}
      <circle cx="44" cy="60" r="7" strokeWidth={1.25} />
      <circle cx="44" cy="60" r="2" strokeWidth={1.25} />
      {/* "+" motif lower-left for "and more" */}
      <line x1="41" y1="78" x2="47" y2="78" strokeWidth={1.25} />
      <line x1="44" y1="75" x2="44" y2="81" strokeWidth={1.25} />
      {/* record glint top-right of body */}
      <circle cx="70" cy="53" r="1.5" fill="url(#gearGold)" stroke="none" />
    </svg>
  );
}
